import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BarChart3, Download } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'react-toastify';
import scadenzarioApi from '../services/scadenzarioApi';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)(() => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const MONTH_OPTIONS = [
  { value: 1, label: 'gennaio' },
  { value: 2, label: 'febbraio' },
  { value: 3, label: 'marzo' },
  { value: 4, label: 'aprile' },
  { value: 5, label: 'maggio' },
  { value: 6, label: 'giugno' },
  { value: 7, label: 'luglio' },
  { value: 8, label: 'agosto' },
  { value: 9, label: 'settembre' },
  { value: 10, label: 'ottobre' },
  { value: 11, label: 'novembre' },
  { value: 12, label: 'dicembre' },
];

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString('it-IT', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

const formatCompactKM = (value, withCurrency = false) => {
  const numericValue = Number(value || 0);
  const abs = Math.abs(numericValue);
  let formatted = '';
  if (abs >= 1000000) {
    formatted = `${(numericValue / 1000000).toFixed(1).replace('.', ',')}M`;
  } else if (abs >= 1000) {
    formatted = `${(numericValue / 1000).toFixed(1).replace('.', ',')}K`;
  } else {
    formatted = numericValue.toLocaleString('it-IT', { maximumFractionDigits: 0 });
  }
  return withCurrency ? `€ ${formatted}` : formatted;
};

function PerformanceTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload || {};
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.2,
        borderRadius: 1.2,
        minWidth: 230,
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.6 }}>
        Clienti: {Number(point.clienti_gestiti || 0).toLocaleString('it-IT')}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Atteso: {formatCurrency(point.atteso_mese)}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Incassato: {formatCurrency(point.incassato_mese)}
      </Typography>
      <Box sx={{ mt: 0.9, pt: 0.9, borderTop: '1px solid #E2E8F0' }}>
        <Typography variant="body2" sx={{ color: '#475569' }}>
          Performance mese: {formatPercent(point.performance_pct)}
        </Typography>
        <Typography variant="body2" sx={{ color: '#475569' }}>
          Delta vs atteso: {formatPercent(point.delta_vs_atteso_pct)}
        </Typography>
      </Box>
    </Paper>
  );
}

const shortenLabel = (value, max = 10) => {
  const text = String(value || '');
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

export default function ScadenzarioKpiPerformanceReferenti() {
  const { addJob } = useJob();
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ referenti: [] });
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    totaleAttesoMese: 0,
    totaleIncassatoMese: 0,
    totaleClientiGestiti: 0,
    performanceMedia: 0,
  });
  const [filters, setFilters] = useState({
    month: currentMonth,
    year: currentYear,
    user_ids: [],
    include_clienti_dettaglio: false,
    pdf_client_filter: 'atteso',
  });

  const availableYears = useMemo(
    () => [currentYear - 2, currentYear - 1, currentYear, currentYear + 1],
    [currentYear]
  );

  const referenteOptions = useMemo(() => meta.referenti || [], [meta.referenti]);

  const titleMonthLabel = useMemo(
    () => MONTH_OPTIONS.find((item) => item.value === Number(filters.month))?.label || '',
    [filters.month]
  );

  const fetchMeta = useCallback(async () => {
    try {
      const res = await scadenzarioApi.getMeta();
      setMeta({ referenti: res.data?.referenti || [] });
    } catch (_error) {
      toast.error('Errore caricamento filtri Performance Referenti');
    }
  }, []);

  const fetchData = useCallback(async (nextFilters) => {
    setLoading(true);
    try {
      const params = {
        month: Number(nextFilters.month),
        year: Number(nextFilters.year),
        include_clienti_dettaglio: nextFilters.include_clienti_dettaglio,
      };
      if (Array.isArray(nextFilters.user_ids) && nextFilters.user_ids.length) {
        params.user_ids = nextFilters.user_ids.join(',');
      }
      const res = await scadenzarioApi.getKpiPerformanceReferenti(params);
      setRows(res.data?.data || []);
      setTotals({
        totaleAttesoMese: Number(res.data?.totaleAttesoMese || 0),
        totaleIncassatoMese: Number(res.data?.totaleIncassatoMese || 0),
        totaleClientiGestiti: Number(res.data?.totaleClientiGestiti || 0),
        performanceMedia: Number(res.data?.performanceMedia || 0),
      });
    } catch (_error) {
      toast.error('Errore caricamento KPI Performance Referenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchData(filters);
  }, [fetchData, filters]);

  const chartData = useMemo(
    () =>
      (rows || []).map((row) => ({
        referente: row.referente_name,
        clienti_gestiti: Number(row.clienti_gestiti || 0),
        atteso_mese: Number(row.atteso_mese || 0),
        incassato_mese: Number(row.incassato_mese || 0),
        performance_pct:
          Number(row.atteso_mese || 0) > 0
            ? (Number(row.incassato_mese || 0) / Number(row.atteso_mese || 0)) * 100
            : 0,
        delta_vs_atteso_pct:
          Number(row.atteso_mese || 0) > 0
            ? ((Number(row.incassato_mese || 0) - Number(row.atteso_mese || 0)) /
                Number(row.atteso_mese || 0)) *
              100
            : 0,
      })),
    [rows]
  );

  const handleDownloadReport = useCallback(async () => {
    try {
      const payload = {
        month: Number(filters.month),
        year: Number(filters.year),
        include_clienti_dettaglio: Boolean(filters.include_clienti_dettaglio),
        pdf_client_filter: filters.pdf_client_filter,
      };
      if (Array.isArray(filters.user_ids) && filters.user_ids.length) {
        payload.user_ids = filters.user_ids.join(',');
      }
      if (filters.include_clienti_dettaglio) {
        const startRes = await scadenzarioApi.startKpiPerformanceReferentiReportJob(payload);
        const jobId = startRes.data?.jobId;
        if (jobId) {
          addJob(jobId, 'scadenzario_performance_referenti_pdf');
          return;
        }
        toast.error('Errore avvio generazione report PDF');
        return;
      }
      const res = await scadenzarioApi.downloadKpiPerformanceReferentiReport(payload);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_performance_incassi_${payload.year}_${String(
        payload.month
      ).padStart(2, '0')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (_error) {
      toast.error('Errore generazione report PDF');
    }
  }, [
    filters.include_clienti_dettaglio,
    filters.month,
    filters.pdf_client_filter,
    filters.user_ids,
    filters.year,
    addJob,
  ]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            color: '#4f46e5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BarChart3 size={20} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Performance Referenti
        </Typography>
      </Box>

      <StyledCard sx={{ p: 2.5, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Mese</InputLabel>
              <Select
                label="Mese"
                value={filters.month}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    month: Number(e.target.value),
                  }))
                }
              >
                {MONTH_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label} {filters.year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Anno</InputLabel>
              <Select
                label="Anno"
                value={filters.year}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    year: Number(e.target.value),
                  }))
                }
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              size="small"
              multiple
              options={referenteOptions}
              value={referenteOptions.filter((item) =>
                filters.user_ids.includes(String(item.id))
              )}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) =>
                String(option.id) === String(value.id)
              }
              onChange={(_event, value) =>
                setFilters((prev) => ({
                  ...prev,
                  user_ids: (value || []).map((item) => String(item.id)),
                }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Referente" placeholder="Tutti i referenti" />
              )}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={3}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={filters.include_clienti_dettaglio}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    include_clienti_dettaglio: e.target.checked,
                  }))
                }
              />
              <Typography variant="body2">Dettaglio clienti</Typography>
            </Box>
          </Grid>
          {filters.include_clienti_dettaglio ? (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Filtro clienti nel PDF
              </Typography>
              <RadioGroup
                value={filters.pdf_client_filter}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    pdf_client_filter: e.target.value,
                  }))
                }
              >
                <FormControlLabel
                  value="atteso"
                  control={<Radio />}
                  label="Tutti i clienti (con atteso mese > 0)"
                />
                <FormControlLabel
                  value="incassato"
                  control={<Radio />}
                  label="Solo clienti con incassato mese > 0"
                />
              </RadioGroup>
            </Grid>
          ) : null}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.2, mt: 2 }}>
          <Button
            variant="contained"
            color="warning"
            startIcon={<Download size={16} />}
            onClick={handleDownloadReport}
            disabled={loading}
          >
            Genera Report PDF
          </Button>
        </Box>
      </StyledCard>

      <Paper sx={{ borderRadius: 1.5, p: 1.5 }}>
        <Tabs value={tab} onChange={(_event, nextTab) => setTab(nextTab)}>
          <Tab label="Grafico Performance" />
          <Tab label="Riepilogo Dettaglio" />
        </Tabs>
      </Paper>

      {loading ? (
        <Paper sx={{ mt: 2, p: 5, borderRadius: 0, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Paper>
      ) : tab === 0 ? (
        <StyledCard sx={{ p: 2.5, mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Grafico Performance - {titleMonthLabel} {filters.year}
          </Typography>
          <Box sx={{ height: 420, overflowX: 'auto', overflowY: 'hidden' }}>
            <Box sx={{ width: Math.max(chartData.length * 82, 900), height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 8, bottom: 10 }}
                  barCategoryGap={18}
                >
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="4 4" vertical={false} />
                  <XAxis
                    dataKey="referente"
                    interval={0}
                    height={62}
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#CBD5E1' }}
                    tickLine={{ stroke: '#CBD5E1' }}
                    tickFormatter={(value) => shortenLabel(value)}
                  />
                  <YAxis
                    yAxisId="amount"
                    width={78}
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#CBD5E1' }}
                    tickLine={{ stroke: '#CBD5E1' }}
                    tickFormatter={(value) => formatCompactKM(value, true)}
                  />
                  <YAxis
                    yAxisId="clients"
                    orientation="right"
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#CBD5E1' }}
                    tickLine={{ stroke: '#CBD5E1' }}
                    tickFormatter={(value) => formatCompactKM(value)}
                  />
                  <Tooltip content={<PerformanceTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 13 }} />
                  <Bar
                    yAxisId="clients"
                    dataKey="clienti_gestiti"
                    name="Clienti Gestiti"
                    fill="#3b82f6"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={30}
                  />
                  <Bar
                    yAxisId="amount"
                    dataKey="atteso_mese"
                    name="Atteso Mese"
                    fill="#f59e0b"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={30}
                  />
                  <Bar
                    yAxisId="amount"
                    dataKey="incassato_mese"
                    name="Incassato Mese"
                    fill="#10b981"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={30}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </StyledCard>
      ) : (
        <StyledCard sx={{ p: 2.5, mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Riepilogo Performance - {titleMonthLabel} {filters.year}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Collaboratore</TableCell>
                  <TableCell align="right">Clienti Gestiti</TableCell>
                  <TableCell align="right">Atteso Mese</TableCell>
                  <TableCell align="right">Incassato Mese</TableCell>
                  <TableCell align="right">% Totale</TableCell>
                  <TableCell align="right">Performance %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.user_id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{row.referente_name}</TableCell>
                    <TableCell align="right">{Number(row.clienti_gestiti || 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.atteso_mese)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.incassato_mese)}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={formatPercent(row.percent_totale)}
                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={formatPercent(row.performance_percent)}
                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Totale</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {Number(totals.totaleClientiGestiti || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {formatCurrency(totals.totaleAttesoMese)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {formatCurrency(totals.totaleIncassatoMese)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {formatPercent(100)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {formatPercent(totals.performanceMedia)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </StyledCard>
      )}
    </Box>
  );
}
